import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';

const ActiveValidationLocksMenu = ({ locks = [], username = '', buttonId = 'validation-locks-button' }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const locksForDisplay = useMemo(() => {
    const locksByEventId = new Map(
      (Array.isArray(locks) ? locks : []).map((lock) => [Number(lock.event_id), lock]),
    );
    return Array.from(locksByEventId.values());
  }, [locks]);

  useEffect(() => {
    if (locksForDisplay.length === 0) {
      setAnchorEl(null);
    }
  }, [locksForDisplay.length]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <Tooltip title={t('validation_workspace.active_validators_tooltip', { count: locksForDisplay.length })} arrow>
        <span>
          <IconButton
            id={buttonId}
            size="small"
            disabled={locksForDisplay.length === 0}
            onClick={(clickEvent) => setAnchorEl(clickEvent.currentTarget)}
            aria-label={t('validation_workspace.active_validators_aria', { count: locksForDisplay.length })}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            sx={{
              minWidth: { xs: 40, md: 36 },
              minHeight: { xs: 40, md: 36 },
              p: 0.75,
              borderRadius: 2,
              color: 'text.secondary',
              '&:not(:disabled):hover': { bgcolor: 'rgba(87, 87, 176, 0.1)', color: 'primary.main' },
            }}
          >
            <Badge badgeContent={locksForDisplay.length} showZero color="primary" max={99}>
              <PersonOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      {locksForDisplay.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{ 'aria-labelledby': buttonId }}
        >
          <MenuItem disabled sx={{ opacity: 1, fontWeight: 700 }}>
            {t('validation_workspace.in_progress')}
          </MenuItem>
          {locksForDisplay.map((lock) => {
            const isCurrentUser = String(lock.username || '').toLowerCase() === String(username || '').toLowerCase();
            return (
              <MenuItem
                key={lock.event_id}
                onClick={() => setAnchorEl(null)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}
              >
                <PersonOutlineIcon fontSize="small" color={isCurrentUser ? 'primary' : 'action'} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={isCurrentUser ? 700 : 500} noWrap>
                    {isCurrentUser ? t('validation_workspace.you') : lock.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{lock.event_id}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Box>
  );
};

export default ActiveValidationLocksMenu;
